import classNames from 'classnames';
import React, { useContext, useMemo } from 'react';

import { HeaderAccountFlyoutProps } from '@zooplus/header-account-flyout';
import { ArrowLoop } from '@zooplus/icons';
import { Anchor, LayoutContainer } from '@zooplus/ui-components';

import AppContext from '../../context';
import { SHIPPING_FEE_TEXT_ID } from '../../utils/constants';
import { SentryBoundary } from '../../utils/SentryBoundary';
import getHeaderAccountFlyout from '../HeaderAccountFlyout/HeaderAccountFlyoutWrapper';
import Link from '../Link';
import { LinkTypeEnum } from '../Link/Link';
import Multilanguage from '../Multilanguage';
import PointsBalance from '../PointsBalance/PointsBalanceWrapper';

import styles from './TopBar.module.scss';

export const HELP_ID = 'srh-help';
export const REORDER_ID = 'srh-reorder';

function Separator({ className }: { className?: string }): React.JSX.Element {
    return <div className={classNames(styles.linkSeparator, className)} />;
}

export default function TopBarComponent(): React.JSX.Element {
    const {
        helpLink,
        translations,
        multilanguage,
        bundle,
        domain,
        translationsLocale,
        isNextJS,
        shippingInfoLink,
        zooClubComponentEnabled,
    } = useContext(AppContext);

    const HeaderAccountFlyout = useMemo(
        () => getHeaderAccountFlyout(isNextJS),
        [],
    );

    return (
        <LayoutContainer
            variant="default"
            data-zta="top-bar-container"
            className={styles.topBar}>
            <div className={styles.desktopSectionContainer}>
                {multilanguage && (
                    <>
                        <Multilanguage
                            className={styles.multilanguageWrapper}
                        />
                        <Separator className={styles.multilanguageSeparator} />
                    </>
                )}
            </div>
            <div
                className={classNames(styles.topBarText, {
                    [styles.multiLanguageText]: multilanguage,
                })}>
                <div
                    className={styles.shippingInfoText}
                    id={SHIPPING_FEE_TEXT_ID}>
                    <Anchor href={shippingInfoLink}>
                        {translations.get('shop.header.delivery.fee.text')}
                    </Anchor>
                </div>
            </div>
            <div className={styles.desktopSectionContainer}>
                <Link
                    id={HELP_ID}
                    href={helpLink}
                    target="_blank"
                    linkType={LinkTypeEnum.NEUTRAL}
                    noWrap>
                    {translations.get('shop.header.top.help')}
                </Link>
                <Separator />
                <Anchor
                    id={REORDER_ID}
                    isBold
                    href="/account/orders/reorder"
                    iconRight={() => (
                        <ArrowLoop
                            title={translations.get('shop.header.top.reorder')}
                        />
                    )}
                    variant="neutral"
                    className={styles.reorderLink}>
                    {translations.get('shop.header.top.reorder')}
                </Anchor>

                {zooClubComponentEnabled && (
                    <>
                        <Separator
                            className={styles.pointsBalanceWrapperSeparator}
                        />
                        <div className={styles.pointsBalanceWrapper}>
                            <PointsBalance />
                        </div>
                    </>
                )}

                <Separator />
                <SentryBoundary componentId="top-bar-account-flyout">
                    <div className={styles.headerAccountFlyoutWrapper}>
                        <HeaderAccountFlyout
                            locale={
                                translationsLocale as HeaderAccountFlyoutProps['locale']
                            }
                            bundle={
                                bundle as HeaderAccountFlyoutProps['bundle']
                            }
                            domain={
                                domain as HeaderAccountFlyoutProps['domain']
                            }
                            hasBonusPoints={zooClubComponentEnabled}
                            onFlyoutClick={() => {}}
                        />
                    </div>
                </SentryBoundary>
            </div>
        </LayoutContainer>
    );
}
